import { type FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Input, Modal } from '@/components/ui'
import type { ClubChallenge } from '@/types'

export interface ChallengeFormValues {
  name: string
  description: string | null
  starts_on: string
  ends_on: string
  target_categories: number | null
}

interface ChallengeFormModalProps {
  title: string
  initial?: ClubChallenge
  isPending: boolean
  error?: boolean
  onSubmit: (values: ChallengeFormValues) => void
  onClose: () => void
}

export function ChallengeFormModal({
  title,
  initial,
  isPending,
  error,
  onSubmit,
  onClose,
}: ChallengeFormModalProps) {
  const { t } = useTranslation()
  const year = new Date().getFullYear()
  const [name, setName] = useState(initial?.name ?? t('challenges.form.defaultName', { year }))
  const [description, setDescription] = useState(initial?.description ?? '')
  const [startsOn, setStartsOn] = useState(initial?.starts_on ?? `${year}-01-01`)
  const [endsOn, setEndsOn] = useState(initial?.ends_on ?? `${year}-12-31`)
  const [target, setTarget] = useState(
    initial?.target_categories ? String(initial.target_categories) : ''
  )

  const datesInvalid = !!startsOn && !!endsOn && endsOn < startsOn

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !startsOn || !endsOn || datesInvalid) return
    const targetNumber = Number.parseInt(target, 10)
    onSubmit({
      name: name.trim(),
      description: description.trim() || null,
      starts_on: startsOn,
      ends_on: endsOn,
      target_categories: Number.isFinite(targetNumber) && targetNumber > 0 ? targetNumber : null,
    })
  }

  const labelClass = 'text-sm font-medium text-foreground'

  return (
    <Modal isOpen onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto p-6">
        <div className="space-y-2">
          <label htmlFor="challenge-name" className={labelClass}>
            {t('challenges.form.name')}
          </label>
          <Input
            id="challenge-name"
            value={name}
            maxLength={100}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <label htmlFor="challenge-start" className={labelClass}>
              {t('challenges.form.startsOn')}
            </label>
            <Input
              id="challenge-start"
              type="date"
              value={startsOn}
              onChange={(e) => setStartsOn(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="challenge-end" className={labelClass}>
              {t('challenges.form.endsOn')}
            </label>
            <Input
              id="challenge-end"
              type="date"
              value={endsOn}
              min={startsOn}
              onChange={(e) => setEndsOn(e.target.value)}
              required
            />
          </div>
        </div>
        {datesInvalid && <p className="text-sm text-error">{t('challenges.form.datesInvalid')}</p>}
        <p className="text-xs text-muted-foreground">{t('challenges.form.periodHint')}</p>

        <div className="space-y-2">
          <label htmlFor="challenge-target" className={labelClass}>
            {t('challenges.form.target')}{' '}
            <span className="font-normal text-muted-foreground">({t('common.optional')})</span>
          </label>
          <Input
            id="challenge-target"
            type="number"
            min={1}
            max={500}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="33"
          />
          <p className="text-xs text-muted-foreground">{t('challenges.form.targetHint')}</p>
        </div>

        <div className="space-y-2">
          <label htmlFor="challenge-description" className={labelClass}>
            {t('challenges.form.description')}{' '}
            <span className="font-normal text-muted-foreground">({t('common.optional')})</span>
          </label>
          <textarea
            id="challenge-description"
            value={description}
            maxLength={1000}
            rows={3}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('challenges.form.descriptionPlaceholder')}
            className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {error && <p className="text-sm text-error">{t('errors.general')}</p>}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isPending || !name.trim() || datesInvalid}>
            {isPending ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
